"use client";

import React, { useState, useRef, useEffect, useCallback, ReactNode } from "react";
import { createPortal } from "react-dom";

type Position = "top" | "right" | "bottom" | "left";

interface PopoverProps {
  position: Position;
  trigger: React.ReactNode;
  children: ReactNode;
  /** Controlled mode: externally control visibility */
  open?: boolean;
  /** Controlled mode: called when the popover should close */
  onClose?: () => void;
  /** Custom width in pixels (default: 300) */
  widthPx?: number;
  /** Render via portal to escape overflow containers (default: false) */
  portal?: boolean;
}

export default function Popover({ position, trigger, children, open, onClose, widthPx = 300, portal = false }: PopoverProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [portalStyle, setPortalStyle] = useState<React.CSSProperties>({});
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;

  const updatePortalPosition = useCallback(() => {
    if (!portal || !triggerRef.current || !isOpen) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const gap = 8;
    let top = 0, left = 0;

    switch (position) {
      case "bottom":
        top = rect.bottom + gap;
        left = rect.left + rect.width / 2 - widthPx / 2;
        break;
      case "top":
        top = rect.top - gap;
        left = rect.left + rect.width / 2 - widthPx / 2;
        break;
      case "right":
        top = rect.top + rect.height / 2;
        left = rect.right + gap;
        break;
      case "left":
        top = rect.top + rect.height / 2;
        left = rect.left - gap - widthPx;
        break;
    }

    setPortalStyle({
      position: "fixed",
      top,
      left,
      width: widthPx,
      zIndex: 99999,
      ...(position === "top" ? { transform: "translateY(-100%)" } : {}),
      ...(position === "right" || position === "left" ? { transform: "translateY(-50%)" } : {}),
    });
  }, [portal, isOpen, position, widthPx]);

  useEffect(() => {
    if (!portal || !isOpen) return;
    updatePortalPosition();
    window.addEventListener("scroll", updatePortalPosition, true);
    window.addEventListener("resize", updatePortalPosition);
    return () => {
      window.removeEventListener("scroll", updatePortalPosition, true);
      window.removeEventListener("resize", updatePortalPosition);
    };
  }, [portal, isOpen, updatePortalPosition]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        if (isControlled) {
          onClose?.();
        } else {
          setInternalOpen(false);
        }
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isControlled, onClose]);

  const togglePopover = () => {
    if (isControlled) {
      if (isOpen) onClose?.();
    } else {
      setInternalOpen(!internalOpen);
    }
  };

  const positionClasses = {
    top: "bottom-full left-1/2 transform -translate-x-1/2 mb-2",
    right: "left-full top-1/2 transform -translate-y-1/2 ml-2",
    bottom: "top-full left-1/2 transform -translate-x-1/2 mt-2",
    left: "right-full top-1/2 transform -translate-y-1/2 mr-2",
  };

  const arrowClasses = {
    top: "bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45",
    right:
      "left-0 top-1/2 transform -translate-y-1/2 -translate-x-1/2 rotate-45",
    bottom:
      "top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rotate-45",
    left: "right-0 top-1/2 transform -translate-y-1/2 translate-x-1/2 rotate-45",
  };

  const popoverContent = (
    <div
      ref={popoverRef}
      className={portal ? "" : `absolute w-[${widthPx}px] z-99999 ${positionClasses[position]}`}
      style={portal ? portalStyle : undefined}
    >
      <div className="w-full bg-white rounded-xl shadow-theme-lg dark:bg-[#1E2634] overflow-hidden">
        {children}
      </div>
      <div
        className={`absolute w-3 h-3 bg-white border border-gray-200 dark:border-gray-700 dark:bg-[#1E2634] ${arrowClasses[position]}`}
        style={{ zIndex: -1 }}
      ></div>
    </div>
  );

  return (
    <div className="relative inline-block">
      <div ref={triggerRef} onClick={togglePopover}>
        {trigger}
      </div>
      {isOpen && (
        portal
          ? createPortal(popoverContent, document.body)
          : popoverContent
      )}
    </div>
  );
}
